<template>
  <div class="text-left">
    <div>
      <template v-if="availableForPurchase && product.logicTradeConfigurable && configuration">
        <span class="block font-bold mt-25 xl:mt-50">
          {{ $t('product.configure') }}
        </span>
        <template
          v-for="step in configuration.steps"
          :key="`step-${step.id}`"
        >
          <template v-if="step.possibleValues?.length && step.possibleValues[0].thumbnailUrl">
            <div class="mt-10 border border-primary-300 p-10 md:p-20 pr-0 md:pr-10">
              <p class="text-base">
                {{ $t('general.select') }} {{ step.description?.toLowerCase() }}
              </p>
              <div class="flex flex-wrap items-center leading-none text-xs md:text-sm">
                <label
                  v-for="value in step.possibleValues"
                  :key="`step-${step.id}-value-${value.key}`"
                  :title="value.value"
                  class="form-radio block w-[75px] h-[75px] mt-10 mr-10 relative rounded-full border border-primary-500/20 cursor-pointer"
                >
                  <input
                    :id="step.id"
                    :name="step.id"
                    type="radio"
                    class="hidden"
                    :value="value.key"
                    :required="step.required"
                    @change="configure"
                  >
                  <img
                    v-if="value.thumbnailUrl"
                    :src="value.thumbnailUrl"
                    class="absolute top-0 left-0 w-full h-full object-contain rounded-full input-img"
                    width="75"
                    height="75"
                    :alt="value.value"
                  >
                  <div
                    v-else
                    class="bg-primary-200 input-img absolute inset-0 rounded-full"
                  />
                </label>
              </div>
            </div>
          </template>
          <label
            v-else-if="step.possibleValues?.length"
            :for="step.id"
            class="relative block mt-10"
          >
            <select
              :id="step.id"
              :name="step.id"
              :placeholder="`${$t('general.select')} ${step.description?.toLowerCase()}`"
              :required="step.required"
              class="p-10 md:p-20 pr-50 md:pr-100 border border-primary-300 w-full flex text-xs sm:text-base outline-none text-primary-700 focus:text-primary-500"
              @change="configure"
            >
              <option
                disabled
                selected
                value=""
              >
                {{ $t('general.select') }} {{ step.description?.toLowerCase() }}
              </option>
              <option
                v-for="value in step.possibleValues"
                :key="`step-${step.id}-value-${value.key}`"
                :value="value.key"
                :selected="step.value === value.key"
              >
                {{ value.value }} ({{ formatCurrency(value.chargeAmount) }})
              </option>
            </select>
            <div class="absolute pointer-events-none h-full p-10 md:p-20 right-0 inset-y-0 bg-white border border-primary-300 text-primary-300">
              <chevronIcon class="w-20" />
            </div>
          </label>

          <!-- @TODO: if colors
          <p class="font-bold mt-25 xl:mt-50">
            Selecteer kleur
          </p>
          <ul class="flex flex-wrap items-center leading-none text-xs md:text-sm">
            <li>
              <button
                class="block mt-10 mr-10 w-40 h-40 rounded-full bg-[#AF6D49] ring-offset-2 ring-2 ring-secondary"
              />
            </li>
            <li>
              <button
                class="block mt-10 mr-10 w-40 h-40 rounded-full bg-[#746F47]"
              />
            </li>
            <li>
              <button
                class="block mt-10 mr-10 w-40 h-40 rounded-full bg-[#5A595D]"
              />
            </li>
            <li>
              <button
                class="block mt-10 mr-10 w-40 h-40 rounded-full bg-[#1F1F1D]"
              />
            </li>
          </ul>
          -->
        </template>

        <ul
          v-if="summary && summary.State > 100"
          class="mt-25 list-inside text-base"
        >
          <li class="font-bold">
            {{ $t('product.configuration') }}
          </li>
          <li
            v-for="step in configuration.steps.filter((step) => step.value)"
            :key="`chosen-step-${step.id}`"
            class="list-disc mt-5"
          >
            {{ step.description }}: {{ step.valueDescription }} ({{ formatCurrency(step.possibleValues.find((value => value.key === step.value)).chargeAmount) }})
          </li>
          <li
            v-if="summary && summary.State > 100"
            class="text-lg font-bold mt-15"
          >
            {{ $t('general.total') }} {{ formatCurrency(summary.Price) }}
          </li>
        </ul>
      </template>
    </div>

    <template v-if="product.logicTradeConfigurable">
      <div class="flex justify-center lg:justify-start mt-20">
        <button
          type="button"
          class="transition-colors bg-primary-300 hover:bg-primary-500 text-white text-xl px-20 py-20 xl:px-25 h-full items-center justify-center flex w-full font-serif font-bold cursor-pointer"
          @click="showDrawer = !showDrawer"
        >
          <span>{{ $t('product.quote') }}</span>
          <chevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
        </button>
      </div>
      <Teleport to="body">
        <drawer
          v-model="showDrawer"
          :product="product"
          :configuration="configuration"
          :data="globalData"
        />
      </Teleport>
    </template>

    <ul class="mt-25 space-y-10">
      <li
        v-if="product?.logicTradeStores?.length"
        class="flex justify-center lg:justify-start items-center space-x-5"
      >
        <eyeIcon />
        <span>{{ $t('product.stores') }} </span>
        <NuxtLink
          :to="`/${stores?.uri}`"
          class="underline hover:no-underline"
        >
          {{ product?.logicTradeStores.map((store) => store.store).join(` ${$t('general.and')} `) }}
        </NuxtLink>
      </li>
      <li class="flex justify-center lg:justify-start">
        {{ defaultVariant?.hasUnlimitedStock && product?.logicTradeRequiredWeeksForDelivery ? $t('product.delivery', { weeks: product?.logicTradeRequiredWeeksForDelivery }) : defaultVariant?.stock || defaultVariant?.hasUnlimitedStock ? $t('product.in-stock') : $t('product.no-stock') }}
      </li>
    </ul>
  </div>
</template>

<script setup>
import {
  useState,
  $fetch,
  formatCurrency,
  computed,
  onMounted,
  useJsonParse,
} from '#imports';

// General icons
import eyeIcon from '@/assets/img/icon-eye.svg';
import chevronIcon from '@/assets/img/icon-chevron.svg';

// Props
const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  interiorAdvice: {
    type: Object,
    default: null,
  },
  globalData: {
    type: Object,
    default: null,
  },
});

// Data
const navigation = useState('navigation');

// Custom data
const configuration = useState('configuration', () => ({}));
const showDrawer = useState('showDrawer', () => false);

// Computed properties
const stores = computed(() => navigation.value?.stores);
const defaultVariant = computed(() => props.product?.variants.find((variant) => variant.isDefault));
const availableForPurchase = computed(() => defaultVariant.value?.availableForPurchase);
const summary = computed(() => useJsonParse(configuration.value.summary));

// Methods
/**
 * Configure product.
 *
 * @param {Event} e
 */
const configure = async (e) => {
  configuration.value = await $fetch('/actions/logictrade/configurator/answer', {
    params: {
      configuration: configuration.value.configurationGuid,
      step: e.target.name,
      value: e.target.value,
    },
  });
};

// Hooks
onMounted(async () => {
  if (availableForPurchase.value && props.product.logicTradeConfigurable) {
    configuration.value = await $fetch('/actions/logictrade/configurator/start', {
      key: 'configurator',
      params: {
        sku: props.product.variants[0].sku,
        price: props.product.defaultPrice,
      },
    });
  }
});
</script>
